import { SidebarFeatureEnum } from 'config/types/sidebar'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useToggleSidebar } from 'state/app/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button'
import Image from 'UIKit/Image'
import Text from 'UIKit/Text'
import Column, { ColumnCenter } from '../Column'
import { RowBetween, RowCenter, RowMiddle } from '../Row'
import SidebarFeatures from './SidebarFeatures'
import TournamentNoticeBadge from './TournamentNoticeBadge'

export const SidebarContainer: React.FC<BoxProps> = ({ children, zIndex, ...props }) => {
  const { isSidebarOpen } = useToggleSidebar()

  return (
    <StyledSidebarContainer
      $Open={isSidebarOpen}
      zIndex={zIndex}
      minHeight="calc(var(--screen-height) - var(--notification-bar-height))"
      maxHeight="calc(var(--screen-height) - var(--notification-bar-height))"
    >
      <StyledInnerContainer $Open={isSidebarOpen} {...props} zIndex={zIndex}>
        {children}
      </StyledInnerContainer>
    </StyledSidebarContainer>
  )
}

export const SidebarToggle: React.FC = () => {
  const { openSidebar, isSidebarOpen } = useToggleSidebar()
  const { t } = useTranslation()

  return (
    <Container id="sidebar-openProcessSideBar">
      <ColumnCenter
        height="56px"
        width="56px"
        style={{ gap: '4px' }}
        background={colors.background}
        borderTopLeftRadius="8px"
        borderBottomLeftRadius="8px"
        justifyContent="center !important"
        position="relative"
      >
        <StyledButton
          variant="text"
          onClick={() => openSidebar(SidebarFeatureEnum.Tournament)}
          data-tooltip-id="common-tip-left"
          data-tooltip-content={t('Tournament')}
          $active={isSidebarOpen}
        >
          <Box width={32} height={32}>
            <Image src="/images/tournament/tournament-icon.png" width={32} height={32} />
          </Box>
          <TournamentNoticeBadge />
        </StyledButton>
      </ColumnCenter>
    </Container>
  )
}

export const Sidebar: React.FC = () => {
  const { isSidebarOpen, featureActive, closeSidebar } = useToggleSidebar()
  const { isMd, isSm, isXs, isMobile } = useMatchBreakpoints()
  const isSidebarCanHide = isMd || isSm || isXs

  useEffect(() => {
    if (!(isSidebarCanHide && isSidebarOpen && isMobile)) {
      return
    }

    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
      window.scrollTo(0, scrollY)
    }
  }, [isSidebarCanHide, isMobile, isSidebarOpen])

  const isTournamentActive = featureActive === SidebarFeatureEnum.Tournament

  return (
    <SidebarContainer id="sidebarnavigation" height={['100%', '', 'calc(100% - 65px)']}>
      <SidebarToggle />
      <TopBar id="sidebar-topbar">
        <RowMiddle>
          {isMobile && (
            <StyledButtonIcon onClick={closeSidebar} display={['flex', 'flex', 'none']} position="relative" mr="8px">
              <Icons.BackIcon width="22px" height="22px" />
              <Box
                position={['absolute', 'absolute', 'unset']}
                left="-20px"
                top="-20px"
                right="-20px"
                bottom="-20px"
                borderRadius="50%"
              />
            </StyledButtonIcon>
          )}
          <Text fontSize="16px" fontWeight="700" letterSpacing={-0.32}>
            {isTournamentActive && <Trans>Tournament</Trans>}
          </Text>
        </RowMiddle>

        <Button
          variant="text"
          height="fit-content !important"
          onClick={closeSidebar}
          display={['none', 'none', 'flex']}
        >
          <Icons.CloseIcon fill={colors.text} width="20px" height="20px" />
        </Button>
      </TopBar>

      <StyledScrollContainer
        minWidth={['320px', '320px', theme.sidebarWidth]}
        maxHeight="calc(100% - 45px)"
        style={{ flex: 1 }}
      >
        {isSidebarOpen && <SidebarFeatures />}
      </StyledScrollContainer>
    </SidebarContainer>
  )
}

const StyledSidebarContainer = styled(Box)<{ $Open: boolean }>`
  position: fixed;
  top: 0px;
  bottom: 0px;
  right: 0;
  width: ${({ $Open }) => ($Open ? '100vw' : '0')};
  transition: ${({ theme: { transitions } }) => transitions.medium};
  z-index: 6;
  background: ${({ theme: { colors } }) => colors.cardBackground};
  height: 100vh;
  padding-bottom: 20px;

  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    top: calc(${({ theme: { topbarHeight } }) => topbarHeight} + var(--notification-bar-height));
    right: 0;
    bottom: 0;
    padding-top: 0px;
    left: unset;
    transition: ${({ theme: { transitions } }) => transitions.fast};
    width: ${({ theme: { sidebarWidth }, $Open }) => ($Open ? `${sidebarWidth}px` : '0')};
    padding-bottom: 0px;
  }
`

const Container = styled(Box)`
  position: absolute;
  z-index: 4;
  top: calc(${({ theme: { topbarHeight } }) => topbarHeight} + var(--notification-bar-height) + 24px);
  left: -56px;

  width: 56px;
  height: 56px;
  padding: 0px;
  transition: ${({ theme: { transitions } }) => transitions.fast};
  display: none;

  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    display: block;
  }
`

const StyledButton = styled(Button)<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  padding: 0px !important;
  border-radius: 4px;
  position: relative;
  background: ${({ $active, theme: { colors } }) => ($active ? colors.backgroundAlt8 : 'transparent')};
  &:hover:not(:disabled):not(:active) {
    transition: none;
    transform: none;
  }
`

const TopBar = styled(RowBetween)`
  border-bottom: 1px solid rgba(84, 84, 86, 0.6);
  cursor: pointer;
  padding: 20px 12px;

  max-height: 60px;

  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    border-bottom: none;
  }
`

const StyledScrollContainer = styled(Box)`
  overflow-y: auto;
  overflow-x: hidden;

  ::-webkit-scrollbar {
    width: 0.9rem;
  }
  scrollbar-width: thin;

  ::-webkit-scrollbar-thumb {
    visibility: hidden;
  }

  &:hover {
    ::-webkit-scrollbar-thumb {
      background-color: #2d3137 !important;
      visibility: visible;
    }
  }
`

const StyledInnerContainer = styled(Column)<{ $Open: boolean }>`
  transition: ease ${({ theme }) => theme.transitions.fast};
`

const StyledButtonIcon = styled(RowCenter)`
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;

  > svg {
    margin-left: 0 !important;
    fill: #fff;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`
